import {
  FormDetailResponse,
  UpdateFormRequest,
  FormDetailResponseSchema,
  UpdateFormRequestSchema,
} from "@codway-dev/shared";
import { AuthService } from "./auth.service";

const API_BASE_URL = "/api"; // Proxy configured in vite.config.ts or Shopify app config

const getShopDomain = () => {
  // 1. Try URL Params
  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get("shop");
  if (shop) return shop;

  // 2. Try App Bridge Config (if available globally)
  const appBridgeShop = window.shopify?.config?.shop || window.shopify?.shop;
  if (appBridgeShop) return appBridgeShop;

  return "";
};

export const FormService = {
  /**
   * Loads the form configuration.
   * 1. Calls forms/ids (Protected) to resolve the Form ID for this Shop.
   * 2. Calls admin/forms/:id (Protected) to get the full config.
   */
  getForm: async (): Promise<FormDetailResponse> => {
    const token = await AuthService.getShopifyToken();
    if (!token) throw new Error("No Shopify token available");

    const shopDomain = getShopDomain();
    if (!shopDomain) throw new Error("Could not determine shop domain");

    // 1. Discovery Phase: Get Form ID via /forms/ids (Protected)
    const idsResponse = await fetch(`${API_BASE_URL}/forms/ids`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!idsResponse.ok) {
      throw new Error(`Failed to fetch form IDs: ${idsResponse.statusText}`);
    }

    const ids = await idsResponse.json();
    const formId = Array.isArray(ids) && ids.length > 0 ? ids[0] : null;

    if (!formId) throw new Error("No active form found for this shop");

    // 2. Fetch Full Configuration
    const response = await fetch(`${API_BASE_URL}/admin/forms/${formId}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to load form: ${response.statusText}`);
    }

    const data = await response.json();
    return FormDetailResponseSchema.parse(data);
  },

  /**
   * Saves updates to the form.
   * PUT /admin/forms/:id
   */
  saveFormConfig: async (
    formId: string,
    data: UpdateFormRequest
  ): Promise<void> => {
    const token = await AuthService.getShopifyToken();
    if (!token) throw new Error("No Shopify token available");

    // Validate payload client-side before sending (optional but good practice)
    const validPayload = UpdateFormRequestSchema.parse(data);

    const response = await fetch(`${API_BASE_URL}/admin/forms/${formId}`, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(validPayload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Failed to save form: ${errorData.message || response.statusText}`
      );
    }
  },
};
